import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, resolveDynamicComponent as _resolveDynamicComponent, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import { computed, ref, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import type { NavigationKey, WorkspaceOption } from '@klausapp/app-navigation/types'
import config from '@/config'
import analytics from '@/utils/analytics'
import i18n from '@/i18n'
import { session } from '@/composables/useSession'
import type { SessionWorkspace } from '@/types/session'
import type { WorkspaceItem } from '@/types/workspace'
import { useWorkspaceList } from '@/composables/useWorkspaceList'
import useProfileMenu from '@/composables/useProfileMenu'
import KeyboardShortcutsModal from '@/modules/shared/Navigation/components/KeyboardShortcutsModal.vue'
import SwitchAccountModal from '@/modules/shared/Navigation/components/SwitchAccountModal.vue'
import useHelpMenu from '@/composables/useHelpMenu'
import { useNavigationSearch } from '@/composables/useNavigationSearch'
import { useHelpMenuItems } from './composables/useHelpMenuItems'
import QErrorCat from './components/QErrorCat.vue'

import type { SidebarConfig } from './types'

import AppWrapper from './AppWrapper.vue'
import useNavigation from './useNavigation'
import ConversationsSidebar from './components/ConversationsSidebar.vue'
import ActivitySidebar from './components/ActivitySidebar.vue'
import CoachingSidebar from './components/CoachingSidebar.vue'
import SettingsSidebar from './components/SettingsSidebar.vue'
import DashboardSidebar from './components/DashboardSidebar.vue'
import UsersWorkspacesSidebar from './components/UsersWorkspacesSidebar.vue'
import TasksSidebar from './components/TasksSidebar.vue'
import ZdNavFlagManager from './components/NavLogo/ZdNavFlagManager.vue'
import useApplicationBanners from './composables/useApplicationBanners'


export default /*@__PURE__*/_defineComponent({
  __name: 'ZuiApplication',
  setup(__props) {

const translations = {
  no_results: i18n.t('pickers.workspaces.no_results'),
  search_placeholder: i18n.t('universal.search'),
  help_tooltip: i18n.t('modules.application.navbar.help'),
  search_no_results: i18n.t('components.command_palette.not_found'),
  'conversations.filters.delete_filter': i18n.t('conversations.filters.delete_filter'),
  'conversations.filters.clone_filter': i18n.t('conversations.filters.clone_filter'),
  'conversations.filters.edit_filter': i18n.t('conversations.filters.edit_filter'),
}

// Reactive state
const route = useRoute()
const router = useRouter()

// Composables
const { getRoute, navItemsMap, currentRouteName } = useNavigation()
const [workspaces] = useWorkspaceList()
const { showAccountSwitchModal, profileMenuItems, onProfileMenuItemClick } = useProfileMenu()
const { helpMenuItems, onHelpMenuItemClick } = useHelpMenuItems()
const { showKeyboardShortcutsModal } = useHelpMenu()
const { globalBanners, onBannerClick } = useApplicationBanners()
const { searchResults, onSearchQueryChange, onSearchResultSelect } = useNavigationSearch()

// Dispatch search results to web component when they change
watch(searchResults, (results) => {
  if (results) {
    const customElement = document.querySelector('zui-navigation')
    if (customElement) {
      const event = new CustomEvent('zui-search-results', {
        detail: results,
        bubbles: true,
      })
      customElement.dispatchEvent(event)
    }
  }
})

const onWorkspaceSwitch = (workspaceId: number) => {
  if (workspaceId === session.workspace.id) return

  analytics.teamSelectionClicked(workspaceId)
  const currentUrl = new URL(`${window.location.origin}${router.resolve({ name: route.name ?? undefined }).href}`)
  currentUrl.searchParams.append('workspace', workspaceId.toString())

  window.location.href = currentUrl.toString()
}

const toWorkspaceOption = (workspace: SessionWorkspace | WorkspaceItem): WorkspaceOption => {
  const { id, name, color } = workspace
  return { id: id.toString(), name, color }
}

const workspaceOptions = computed(() => workspaces.value.map(toWorkspaceOption))
const hideNavigation = computed(() => !!route.meta?.hideNav)

const getDefaultSidebarConfig = (): SidebarConfig => ({
  action: {
    tooltip: '',
    menuItems: [],
    onClick: () => {
      /* no-op */
    },
  },
  title: '',
  hideToggleSubnavButton: true,
  items: [],
  handleSubnavItemClick: () => {
    /* no-op */
  },
  handleSubnavItemActionClick: () => {
    /* no-op */
  },
  handleGroupActionClick: () => {
    /* no-op */
  },
})

const sidebarConfig = ref<SidebarConfig>(getDefaultSidebarConfig())

const sidebarComponents = {
  conversations: ConversationsSidebar,
  activity: ActivitySidebar,
  coaching: CoachingSidebar,
  tasks: TasksSidebar,
  dashboard: DashboardSidebar,
  settings: SettingsSidebar,
  'users-workspaces': UsersWorkspacesSidebar,
} as const

const sidebarComponent = computed(
  () => sidebarComponents[currentRouteName.value as keyof typeof sidebarComponents] || null,
)

const updateSidebarConfig = (config: SidebarConfig) => {
  sidebarConfig.value = config
}

// Reset to default config when no sidebar component is active
watch(
  sidebarComponent,
  (component) => {
    if (!component) {
      sidebarConfig.value = getDefaultSidebarConfig()
    }
  },
  { immediate: true },
)

const onNavigationClick = (key: NavigationKey): void => {
  const returnedRoute = getRoute(key)

  if (returnedRoute) {
    router.push(returnedRoute)
  }

  analytics.navbarEvent('Clicked navbar', key[0].toUpperCase() + key.slice(1))

  // Handle special case for conversations route
  if (key === 'conversations') {
    const { singleTicket, ...query } = route.query
    if (singleTicket) {
      router.replace({ query })
    }
  }
}

return (_ctx: any,_cache: any) => {
  const _component_router_view = _resolveComponent("router-view")!

  return (hideNavigation.value)
    ? (_openBlock(), _createBlock(_component_router_view, { key: 0 }))
    : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
        _createCommentVNode(" eslint-disable-next-line vue/no-undef-components "),
        _createVNode(AppWrapper, {
          "active-item": _unref(currentRouteName),
          "nav-items-map": _unref(navItemsMap),
          "workspace-options": workspaceOptions.value,
          "active-workspace": toWorkspaceOption(_unref(session).workspace),
          translations: translations,
          "help-menu-items": _unref(helpMenuItems),
          "subnav-title": sidebarConfig.value.title,
          "subnav-items": sidebarConfig.value.items,
          "subnav-action-tooltip": sidebarConfig.value.action?.tooltip || '',
          "subnav-action-menu-items": sidebarConfig.value.action?.menuItems,
          "profile-menu-items": _unref(profileMenuItems),
          "hide-toggle-subnav-button": !!sidebarConfig.value?.hideToggleSubnavButton,
          "global-banners": _unref(globalBanners),
          onNavItemClick: onNavigationClick,
          onSubnavItemClick: sidebarConfig.value.handleSubnavItemClick,
          onSubnavItemActionClick: sidebarConfig.value.handleSubnavItemActionClick,
          onSubnavExtraClick: sidebarConfig.value.handleGroupActionClick,
          onHelpMenuItemClick: _unref(onHelpMenuItemClick),
          onWorkspaceClick: onWorkspaceSwitch,
          onProfileMenuClick: _unref(onProfileMenuItemClick),
          onSubnavActionClick: sidebarConfig.value.action?.onClick,
          onBannerClick: _unref(onBannerClick),
          onSearchQueryChange: _unref(onSearchQueryChange),
          onSearchResultSelect: _unref(onSearchResultSelect)
        }, null, 8 /* PROPS */, ["active-item", "nav-items-map", "workspace-options", "active-workspace", "help-menu-items", "subnav-title", "subnav-items", "subnav-action-tooltip", "subnav-action-menu-items", "profile-menu-items", "hide-toggle-subnav-button", "global-banners", "onSubnavItemClick", "onSubnavItemActionClick", "onSubnavExtraClick", "onHelpMenuItemClick", "onProfileMenuClick", "onSubnavActionClick", "onBannerClick", "onSearchQueryChange", "onSearchResultSelect"]),
        _createCommentVNode(" Renderless sidebar component for state management "),
        (sidebarComponent.value)
          ? (_openBlock(), _createBlock(_resolveDynamicComponent(sidebarComponent.value), {
              key: 0,
              "onUpdate:config": updateSidebarConfig
            }, null, 32 /* NEED_HYDRATION */))
          : _createCommentVNode("v-if", true),
        _createCommentVNode(" Feature flags modal "),
        (_unref(config).flagManager)
          ? (_openBlock(), _createBlock(ZdNavFlagManager, { key: 1 }))
          : _createCommentVNode("v-if", true),
        _createCommentVNode(" Modal for switching the account "),
        _createVNode(SwitchAccountModal, {
          show: _unref(showAccountSwitchModal),
          onClose: _cache[0] || (_cache[0] = ($event: any) => (showAccountSwitchModal.value = false))
        }, null, 8 /* PROPS */, ["show"]),
        _createCommentVNode(" Modal with keybord shortcuts preview "),
        _createVNode(KeyboardShortcutsModal, {
          visible: _unref(showKeyboardShortcutsModal),
          onClose: _cache[1] || (_cache[1] = ($event: any) => (showKeyboardShortcutsModal.value = false))
        }, null, 8 /* PROPS */, ["visible"]),
        _createVNode(QErrorCat)
      ], 64 /* STABLE_FRAGMENT */))
}
}

})